import { Button, Empty, Space, Tooltip, Typography, message } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import RCTable from 'components/custom-table/RCTable';
import { ACL } from 'components/common';
import { getClientList } from 'store/clientSlice';
import { getFormatedDayDateAndTime } from 'utils';
import { getUIVersion } from 'utils/router-static-data';
const { Text } = Typography;

function HighValueIcon({ isHighValue, onClick }) {
	const iconColor = isHighValue ? '#EC221F' : '#007AFF';
	const iconButton = (
		<Button
			type="text"
			size="small"
			onClick={onClick}
			style={{
				padding: 0,
				width: '24px',
				height: '24px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<svg
				width="20"
				height="20"
				viewBox="0 0 16 16"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M10 2.66634H3.33333V13.333H12.6667V5.33301H10V2.66634ZM2 1.99434C2 1.62901 2.298 1.33301 2.666 1.33301H10.6667L14 4.66634V13.995C14.0006 14.0826 13.984 14.1694 13.951 14.2505C13.9181 14.3316 13.8695 14.4054 13.808 14.4678C13.7466 14.5301 13.6734 14.5797 13.5928 14.6138C13.5121 14.6479 13.4255 14.6657 13.338 14.6663H2.662C2.48692 14.6651 2.31934 14.5951 2.19548 14.4713C2.07161 14.3476 2.0014 14.1801 2 14.005V1.99434ZM7.33333 7.33301V5.33301H8.66667V7.33301H10.6667V8.66634H8.66667V10.6663H7.33333V8.66634H5.33333V7.33301H7.33333Z"
					fill={iconColor}
				/>
			</svg>
		</Button>
	);

	// Show tooltip only when red (isHighValue is false)
	if (isHighValue) {
		return <Tooltip title="High Value Advance Request">{iconButton}</Tooltip>;
	}

	return iconButton;
}

HighValueIcon.propTypes = {
	isHighValue: PropTypes.bool,
	onClick: PropTypes.func,
};

// eslint-disable-next-line react/no-multi-comp
function PendingTripsTab({ tableDatas, togglePaginate, dispatchAction }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isV2UI = getUIVersion() === 'new';
	// eslint-disable-next-line sonarjs/cognitive-complexity
	async function mapAdhocToIntercityForm(record) {
		const tripDate = record.expected_trip_date
			? moment(record.expected_trip_date).toDate()
			: undefined;

		let city = '';
		let project = null;
		try {
			const res = await dispatch(
				getClientList({
					search_type: 'project',
					filter_by: {
						id: record.project_id,
					},
				})
			);
			project = res?.data?.[0];
			city = project?.city || '';
		} catch (error) {
			console.error('Error fetching project city:', error);
		}

		return {
			trip_type: 'adhoc',
			city,
			project_id: record.project_id || '',
			project_name:
				record.project_name || project?.project_name || project?.project_code || '',
			route_id: record.route_id ? String(record.route_id) : '',
			route_name: record.route_name || record.route?.route_name || record.route_code || '',

			vendor_id: record.vendor_id || '',
			vendor_type: record.vendor_type || 'regular',
			vehicle_number: record.vehicle_number || '',
			reporting_date: tripDate,
			start_date: tripDate,
			start_location_pincode: record.source_pincode || '',
			end_location_pincode: record.destination_pincode || '',
			vehicle_type: record.vehicle_type || '',
			driver_name: record.driver_name || '',
			driver_number: record.driver_number || '',
			requirement_proof: record.proof_of_request_url ? [record.proof_of_request_url] : [],
			client_rate: record.client_rate || '',
			vendor_rate: record.vendor_rate || '',
			adhoc_request_id: record.id || '',
			remarks: record.remarks || '',
		};
	}
	const handleIconClick = async (record) => {
		if (
			record?.is_high_value_advance === true &&
			!record?.vp_approval_status?.includes('Approved')
		) {
			message.error('High value advance request, waiting for approval');
		} else {
			const formData = await mapAdhocToIntercityForm(record);
			navigate('/trips/add-intercity', {
				state: {
					formData,
					source: 'adhoc-request',
				},
			});
		}
	};

	const columns = [
		{
			title: 'Request ID',
			dataIndex: 'id',
			key: 'id',
			width: 100,
		},
		{
			title: 'Project',
			dataIndex: 'project_code',
			key: 'project_code',
			width: 150,
			render: (project_code, record) => {
				if (isV2UI) {
					return record?.project_name || '-';
				}
				return project_code || '-';
			},
		},
		{
			title: 'Client',
			dataIndex: 'client_name',
			key: 'client_name',
			width: 120,
		},
		{
			title: 'Movement',
			dataIndex: 'movement_type',
			key: 'movement_type',
			width: 120,
			render: (movement_type) => (
				<Text
					style={{
						backgroundColor: '#FFDDB3D9',
						color: '#000000',
						padding: '4px 8px',
						borderRadius: '12px',
						fontSize: '12px',
						fontWeight: '500',
						textAlign: 'center',
						display: 'inline-block',
					}}
				>
					{movement_type || 'Intercity'}
				</Text>
			),
		},
		{
			title: 'Vendor',
			dataIndex: 'vendor_name',
			key: 'vendor_name',
			width: 120,
		},
		{
			title: 'Trip Date/Time',
			dataIndex: 'expected_trip_date',
			key: 'expected_trip_date',
			width: 150,
			render: (date) => {
				if (!date) return '-';
				return `${getFormatedDayDateAndTime(date, 'trip_date').fomatedDate} ${
					getFormatedDayDateAndTime(date, 'trip_date').formatedTimeAMPM
				}`;
			},
		},
		{
			title: 'From',
			key: 'from_location',
			width: 120,
			render: (record) => {
				const parts = [record.source_city, record.source_state].filter(Boolean);
				return parts.join(', ') || '-';
			},
		},
		{
			title: 'To',
			key: 'to_location',
			width: 120,
			render: (record) => {
				const parts = [record.destination_city, record.destination_state].filter(Boolean);
				return parts.join(', ') || '-';
			},
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			width: 120,
			render: (_, record) => (
				<Space size="small">
					<ACL action="create" module="trip_adhoc_request">
						<HighValueIcon
							isHighValue={
								record?.is_high_value_advance === true &&
								!record?.vp_approval_status?.includes('Approved')
							}
							onClick={() => handleIconClick(record)}
						/>
					</ACL>
				</Space>
			),
		},
	];

	return (
		<div style={{ marginTop: 16 }}>
			<RCTable
				rowKey="id"
				tableDatas={tableDatas}
				togglePaginate={togglePaginate}
				dispatchAction={dispatchAction}
				columns={columns}
				scroll={{ x: true }}
				locale={{
					emptyText: (
						<Empty description={<Text type="secondary">No pending trips found</Text>} />
					),
				}}
			/>
		</div>
	);
}

PendingTripsTab.propTypes = {
	tableDatas: PropTypes.array.isRequired,
	togglePaginate: PropTypes.array.isRequired,
	dispatchAction: PropTypes.func.isRequired,
};

export default PendingTripsTab;
